import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, EyeIcon, FilterIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
const OrdersList = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Load orders
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate('/login');
      return;
    }
    // Get orders from localStorage
    const savedOrders = localStorage.getItem('orders');
    if (savedOrders) {
      const parsedOrders = JSON.parse(savedOrders);
      setOrders(parsedOrders);
      setFilteredOrders(parsedOrders);
    }
    setIsLoading(false);
  }, [user, navigate]);
  // Filter orders
  useEffect(() => {
    let result = orders;
    // Filter by status
    if (filterStatus !== 'all') {
      result = result.filter(order => order.status === filterStatus);
    }
    // Filter by search term
    if (searchTerm) {
      result = result.filter(order => order.id.toLowerCase().includes(searchTerm.toLowerCase()) || order.customerName && order.customerName.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setFilteredOrders(result);
  }, [orders, filterStatus, searchTerm]);
  // Handle status filter change
  const handleStatusFilterChange = e => {
    setFilterStatus(e.target.value);
  };
  // Handle search
  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1621">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-1622"></div>
      </div>;
  }
  return <div className="bg-amber-50 min-h-screen" data-id="element-1623">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1624">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6" data-id="element-1625">
          <h1 className="text-2xl font-semibold text-amber-900 mb-4 sm:mb-0" data-id="element-1626">
            Orders
          </h1>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1627">
            <Link to="/add-order" className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900 flex items-center justify-center" data-id="element-1628">
              <PlusIcon className="h-5 w-5 mr-1" data-id="element-1629" />
              New Order
            </Link>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-id="element-1630">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1631">
            <div className="flex-1" data-id="element-1632">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1633">
                Search
              </label>
              <div className="relative" data-id="element-1634">
                <input type="text" id="search" placeholder="Search by order ID or customer name" value={searchTerm} onChange={handleSearch} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500" data-id="element-1635" />
              </div>
            </div>
            <div className="w-full sm:w-48" data-id="element-1636">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1637">
                Status
              </label>
              <select id="status-filter" value={filterStatus} onChange={handleStatusFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500" data-id="element-1638">
                <option value="all" data-id="element-1639">All Statuses</option>
                <option value="Pending" data-id="element-1640">Pending</option>
                <option value="Confirmed" data-id="element-1641">Confirmed</option>
                <option value="Preparing" data-id="element-1642">Preparing</option>
                <option value="Out for Delivery" data-id="element-1643">Out for Delivery</option>
                <option value="Delivered" data-id="element-1644">Delivered</option>
                <option value="Cancelled" data-id="element-1645">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
        {/* Orders List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1646">
          {filteredOrders.length === 0 ? <div className="p-8 text-center" data-id="element-1647">
              <div className="text-amber-800 text-lg font-medium mb-2" data-id="element-1648">
                No orders found
              </div>
              <p className="text-gray-600" data-id="element-1649">
                {orders.length === 0 ? "You haven't placed any orders yet." : 'No orders match your current filters.'}
              </p>
              {orders.length === 0 && <Link to="/add-order" className="mt-4 inline-block px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-1650">
                  Create your first order
                </Link>}
            </div> : <div className="overflow-x-auto" data-id="element-1651">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1652">
                <thead className="bg-gray-50" data-id="element-1653">
                  <tr data-id="element-1654">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1655">
                      Order ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1656">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1657">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1658">
                      Items
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1659">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1660">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1661">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1662">
                  {filteredOrders.map(order => <tr key={order.id} data-id="element-1663">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1664">
                        {order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1665">
                        {new Date(order.date).toLocaleDateString()}
                        <div className="text-xs text-gray-400" data-id="element-1666">
                          {new Date(order.date).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1667">
                        {order.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1668">
                        {order.items.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1669">
                        ₱{order.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1670">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : order.status === 'Preparing' || order.status === 'Confirmed' ? 'bg-yellow-100 text-yellow-800' : order.status === 'Cancelled' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1671">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1672">
                        <Link to={`/order/${order.id}`} className="text-amber-800 hover:text-amber-900" data-id="element-1673">
                          <EyeIcon className="h-5 w-5" data-id="element-1674" />
                        </Link>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
};
export default OrdersList;